import { useState, useEffect, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Button } from '../ui/button';
import { FormField, Input, Label } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { DateInput } from '../ui/date-input';
import { Select } from '../ui/select-custom';
import { toISODate, fromISODate } from '../../lib/date';
import { formatCurrency } from '../../lib/format';
import { availableQuantityAt } from '../../lib/investments';
import type { PortfolioInput } from '../../lib/investments';
import { earningKinds } from '../../types';
import type { Earning, EarningKind, InvestmentAsset } from '../../types';
import type { ComponentProps } from 'react';

/** JCP é tributado exclusivamente na fonte a 15%. */
const JCP_RATE = 0.15;

export interface EarningFormData {
  assetId: string;
  kind: EarningKind;
  paymentDate: Date;
  quantityBase: number;
  valuePerShare: number;
  grossValue: number;
  irrfWithheld: number;
}

export interface EarningFormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: EarningFormData) => void;
  onClose?: () => void;
  initialData?: Earning | null;
  assets: InvestmentAsset[];
  portfolio: PortfolioInput;
  defaultAssetId?: string;
}

const KIND_HINTS: Record<EarningKind, string> = {
  dividendo: 'Isento. Vai na ficha de Rendimentos Isentos e Não Tributáveis.',
  jcp: 'Tributado exclusivamente na fonte (15% retido). Ficha de Tributação Exclusiva.',
  rendimento_fii: 'Isento para o cotista pessoa física. Ficha de Rendimentos Isentos.',
  amortizacao: 'Não é rendimento: devolve principal e reduz seu custo médio.',
  restituicao_capital: 'Não é rendimento: reduz o custo de aquisição da posição.',
  bonificacao:
    'Aumenta a quantidade. Informe as novas ações em "quantidade" e o custo informado pela empresa em "valor" (0 se foi sem custo).',
};

export function EarningForm({
  onSubmit,
  onClose,
  initialData,
  assets,
  portfolio,
  defaultAssetId,
  ...props
}: EarningFormProps) {
  const [assetId, setAssetId] = useState(
    initialData?.assetId ?? defaultAssetId ?? assets[0]?.id ?? ''
  );
  const [kind, setKind] = useState<EarningKind>(initialData?.kind ?? 'dividendo');
  const [dateISO, setDateISO] = useState(
    toISODate(initialData?.paymentDate ?? new Date())
  );
  const [quantityBase, setQuantityBase] = useState(
    initialData?.quantityBase ? String(initialData.quantityBase) : ''
  );
  const [valuePerShare, setValuePerShare] = useState(initialData?.valuePerShare ?? 0);
  const [grossValue, setGrossValue] = useState(initialData?.grossValue ?? 0);
  const [irrfWithheld, setIrrfWithheld] = useState(initialData?.irrfWithheld ?? 0);
  /** Enquanto o usuário não editar o IRRF à mão, o JCP calcula sozinho. */
  const [irrfTouched, setIrrfTouched] = useState(!!initialData);

  const isEditing = !!initialData;

  useEffect(() => {
    setAssetId(initialData?.assetId ?? defaultAssetId ?? assets[0]?.id ?? '');
    setKind(initialData?.kind ?? 'dividendo');
    setDateISO(toISODate(initialData?.paymentDate ?? new Date()));
    setQuantityBase(initialData?.quantityBase ? String(initialData.quantityBase) : '');
    setValuePerShare(initialData?.valuePerShare ?? 0);
    setGrossValue(initialData?.grossValue ?? 0);
    setIrrfWithheld(initialData?.irrfWithheld ?? 0);
    setIrrfTouched(!!initialData);
  }, [initialData, defaultAssetId, assets]);

  const selectedAsset = assets.find((a) => a.id === assetId);
  const date = useMemo(() => (dateISO ? fromISODate(dateISO) : null), [dateISO]);
  const isBonus = kind === 'bonificacao';

  // Só oferece os tipos de provento que existem para ação ou para FII.
  const kindOptions = useMemo(() => {
    const type = selectedAsset?.type;
    return (Object.keys(earningKinds) as EarningKind[])
      .filter((k) => !type || earningKinds[k].appliesTo.includes(type))
      .map((k) => ({ value: k, label: earningKinds[k].label }));
  }, [selectedAsset?.type]);

  // Troca de papel pode invalidar o tipo escolhido (ex: JCP num FII).
  useEffect(() => {
    if (kindOptions.length > 0 && !kindOptions.some((o) => o.value === kind)) {
      setKind(kindOptions[0]!.value);
    }
  }, [kindOptions, kind]);

  // Sugere a quantidade pela posição na data do pagamento.
  const positionAtDate = useMemo(() => {
    if (!assetId || !date) return 0;
    return availableQuantityAt(portfolio, assetId, date);
  }, [portfolio, assetId, date]);

  const quantityNumber = Number(quantityBase.replace(',', '.')) || 0;

  // Valor por cota x quantidade preenche o bruto automaticamente.
  useEffect(() => {
    if (isBonus) return;
    if (valuePerShare > 0 && quantityNumber > 0) {
      setGrossValue(Number((valuePerShare * quantityNumber).toFixed(2)));
    }
  }, [valuePerShare, quantityNumber, isBonus]);

  useEffect(() => {
    if (kind === 'jcp' && !irrfTouched) {
      setIrrfWithheld(Number((grossValue * JCP_RATE).toFixed(2)));
    } else if (kind !== 'jcp' && !irrfTouched) {
      setIrrfWithheld(0);
    }
  }, [kind, grossValue, irrfTouched]);

  // Renda fixa não paga provento: a remuneração está no preço do título.
  const isFixedIncome = selectedAsset?.type === 'tesouro';

  const isFormValid =
    assetId !== '' &&
    !isFixedIncome &&
    !!date &&
    (isBonus ? quantityNumber > 0 : grossValue > 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !date) return;
    onSubmit({
      assetId,
      kind,
      paymentDate: date,
      quantityBase: quantityNumber,
      valuePerShare,
      grossValue,
      irrfWithheld,
    });
    if (!isEditing) {
      setQuantityBase('');
      setValuePerShare(0);
      setGrossValue(0);
      setIrrfWithheld(0);
      setIrrfTouched(false);
    }
  };

  return (
    <form
      data-slot="earning-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label>
            Papel <span className="text-destructive">*</span>
          </Label>
          <Select
            value={assetId}
            onChange={setAssetId}
            options={assets.map((a) => ({ value: a.id, label: a.ticker }))}
            placeholder="Selecione o papel"
            className="w-full justify-between"
          />
        </FormField>

        <FormField>
          <Label>
            Tipo de provento <span className="text-destructive">*</span>
          </Label>
          <Select
            value={kind}
            onChange={(value) => setKind(value as EarningKind)}
            options={kindOptions}
            className="w-full justify-between"
          />
        </FormField>
      </div>

      {isFixedIncome ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          <span>
            {selectedAsset?.ticker} é renda fixa e não paga provento — a remuneração
            está embutida no preço do título. Atualize o valor atual no cadastro do
            papel.
          </span>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" />
          <span>{KIND_HINTS[kind]}</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="payment-date">
            Data do pagamento <span className="text-destructive">*</span>
          </Label>
          <DateInput id="payment-date" value={dateISO} onChange={setDateISO} />
          <span className="text-xs text-muted-foreground">
            É a data do crédito que define o ano do IRPF.
          </span>
        </FormField>

        <FormField>
          <Label htmlFor="quantityBase">
            Quantidade {isBonus && <span className="text-destructive">*</span>}
          </Label>
          <div className="flex gap-2">
            <Input
              id="quantityBase"
              value={quantityBase}
              placeholder={isBonus ? 'Ações recebidas' : String(positionAtDate || '')}
              inputMode="decimal"
              onChange={(e) => setQuantityBase(e.target.value.replace(/[^0-9.,]/g, ''))}
            />
            {!isBonus && positionAtDate > 0 && (
              <Button
                type="button"
                variant="outline"
                size="md"
                className="shrink-0 whitespace-nowrap px-3 text-xs"
                onClick={() => setQuantityBase(String(positionAtDate))}
                title="Usar a posição na data"
              >
                {positionAtDate}
              </Button>
            )}
          </div>
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {!isBonus && (
          <FormField>
            <Label htmlFor="valuePerShare">Valor por cota/ação</Label>
            <CurrencyInput
              id="valuePerShare"
              value={valuePerShare}
              onChange={setValuePerShare}
            />
            <span className="text-xs text-muted-foreground">
              Preenche o valor total automaticamente.
            </span>
          </FormField>
        )}

        <FormField>
          <Label htmlFor="grossValue">
            {isBonus ? 'Custo informado pela empresa' : 'Valor bruto total'}
            {!isBonus && <span className="text-destructive"> *</span>}
          </Label>
          <CurrencyInput id="grossValue" value={grossValue} onChange={setGrossValue} />
        </FormField>
      </div>

      {(kind === 'jcp' || irrfWithheld > 0) && (
        <FormField>
          <Label htmlFor="earning-irrf">IRRF retido</Label>
          <CurrencyInput
            id="earning-irrf"
            value={irrfWithheld}
            onChange={(value) => {
              setIrrfTouched(true);
              setIrrfWithheld(value);
            }}
          />
          <span className="text-xs text-muted-foreground">
            {kind === 'jcp'
              ? 'Calculado a 15% do bruto. Ajuste se a nota mostrar outro valor.'
              : 'Informe se houve retenção.'}
          </span>
        </FormField>
      )}

      {grossValue > 0 && irrfWithheld > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-sm">
          <span className="text-muted-foreground">Líquido recebido</span>
          <span className="font-semibold text-foreground">
            R$ {formatCurrency(grossValue - irrfWithheld)}
          </span>
        </div>
      )}

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          {isEditing ? 'Salvar alterações' : 'Registrar provento'}
        </Button>
      </div>
    </form>
  );
}
